import { useState } from 'react';
import { Form, Button, Alert, Stack, Table, Badge } from 'react-bootstrap';
import { ApiError } from '@/services/api';
import { useAsync } from '@/common/hooks/use-async';
import { useAuth } from '@/common/hooks/use-auth';
import type { FeedbackRow, ListResponse } from '@/common/types';

/** Outcome options per subject type (match the backend feedback.py validation). */
const PR_OUTCOMES = [
  { value: 'MERGED', label: 'Good — merged as-is' },
  { value: 'CHANGES_REQUESTED', label: 'Needed changes' },
  { value: 'DECLINED', label: 'Bad — declined' },
];
const EXCEPTION_OUTCOMES = [
  { value: 'APPROVED', label: 'Approved — valid exception' },
  { value: 'REJECTED', label: 'Rejected' },
];

const OUTCOME_VARIANT: Record<string, string> = {
  MERGED: 'success', APPROVED: 'success',
  DECLINED: 'danger', REJECTED: 'danger',
  CHANGES_REQUESTED: 'warning',
};

const SOURCE_LABEL: Record<string, string> = {
  human: 'UI', auto: 'auto', 'pr-comment': 'PR comment',
};

/**
 * Engineer feedback on a PR or exception — learning-loop input. Renders prior feedback
 * for the subject plus a submit form. Mirrors the exception-detail write-action pattern
 * (verdict + comment → POST with actor → Alert → reload).
 */
export function FeedbackControl({
  subjectType,
  subjectId,
  repo,
  submit,
  listFeedback,
}: {
  subjectType: 'PR' | 'EXCEPTION';
  subjectId: string;
  repo: string;
  submit: (subjectId: string, repo: string, actor: string, outcome: string, comment: string) => Promise<unknown>;
  listFeedback: (subjectId: string) => Promise<ListResponse<FeedbackRow>>;
}) {
  const { actor } = useAuth();
  const { data, loading, error, reload } = useAsync(() => listFeedback(subjectId), [subjectId]);
  const options = subjectType === 'EXCEPTION' ? EXCEPTION_OUTCOMES : PR_OUTCOMES;

  const [outcome, setOutcome] = useState(options[0].value);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function send() {
    setBusy(true);
    setMsg(null);
    try {
      await submit(subjectId, repo, actor, outcome, comment.trim());
      setMsg({ ok: true, text: 'Feedback recorded — it will be distilled into learned context.' });
      setComment('');
      reload();
    } catch (e) {
      const t = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setMsg({ ok: false, text: `Submit failed — ${t}` });
    } finally {
      setBusy(false);
    }
  }

  const items = data?.items ?? [];

  return (
    <fieldset className="border rounded p-3 mb-4">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">Feedback</legend>
      <p className="text-muted small">
        Tell vuln-sentinel how it did. Feedback is distilled into per-repo learned context the
        agent reloads on future runs. The comment is the signal — explain <em>why</em>.
      </p>

      {msg && <Alert variant={msg.ok ? 'success' : 'danger'}>{msg.text}</Alert>}

      <Stack gap={2}>
        <Form.Group>
          <Form.Label>Verdict</Form.Label>
          <Form.Select value={outcome} onChange={(e) => setOutcome(e.target.value)} disabled={busy}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Comment</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. don't edit vendored files — bump OPEN_TOFU_VERSION instead"
            disabled={busy}
          />
        </Form.Group>
        <div>
          <Button type="button" onClick={send} disabled={busy || !comment.trim()}>
            {busy ? 'Submitting…' : 'Submit feedback'}
          </Button>
        </div>
      </Stack>

      <hr />
      <h6 className="text-muted">History</h6>
      {loading && <div className="text-muted small">Loading…</div>}
      {error && <Alert variant="danger" className="py-1 px-2 small mb-0">{error}</Alert>}
      {!loading && !error && (
        <Table responsive size="sm" className="mb-0">
          <thead>
            <tr><th>When</th><th>Verdict</th><th>Comment</th><th>By</th><th>Source</th></tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.feedbackId}>
                <td>{f.createdAt ? new Date(f.createdAt * 1000).toLocaleString() : '-'}</td>
                <td><Badge bg={OUTCOME_VARIANT[f.outcome] ?? 'secondary'}>{f.outcome}</Badge></td>
                <td>{f.comment || <span className="text-muted">—</span>}</td>
                <td className="small">{f.authorId ?? '-'}</td>
                <td className="small text-muted">{SOURCE_LABEL[f.source ?? ''] ?? f.source ?? '-'}</td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={5} className="text-muted small">No feedback yet.</td></tr>
            )}
          </tbody>
        </Table>
      )}
    </fieldset>
  );
}
